"use client";

import cn from "@utils/cn";
import Image from "next/image";
import Link from "next/link";
import { FC, useState } from "react";

import { CustomLink, HubTemplateHeroProps } from "@/types";

import Button, { ButtonSize, ButtonTheme, ButtonVariant } from "../ui/Button/Button";

const HubTemplateHero: FC<HubTemplateHeroProps> = ({ entry }) => {
  const { image, logo, title, abstract, cta, podcast } = entry;
  const [isChecked, setIsChecked] = useState(podcast?.tabs[0].id ?? "");
  const isActiveClass = "text-black golden-gradient";
  const centeredLogo = entry.logo && (!entry.podcast || entry.podcast?.tabs.length < 1);
  const logoVariant = entry.variant?.indexOf("logo") !== -1;
  const titleOrImageVariant = entry.variant === "title" || entry.variant === "image";
  const showTitle = titleOrImageVariant && entry.title;
  const showAbstract = titleOrImageVariant && entry.abstract;

  return (
    <div
      className={cn("px-s pt-[40px] md:p-0", image && "relative bg-cover bg-center text-white h-[calc(100dvh_-_50px)] md:h-auto md:aspect-[2/1]")}
      style={image && { backgroundImage: `url(${image.url})` }}
    >
      <div className={cn("px-s pb-[40px] md:px-0 md:pb-0 md:w-20\\/24 md:mx-auto text-center", image ? "pt-3xl md:pt-5xl" : "md:pt-xxl ")}>
        <div
          className={cn(
            !image
              ? "md:flex md:items-center md:justify-center md:relative md:mb-l"
              : "bg-[linear-gradient(180deg,_rgba(0,_0,_0,_0)_0%,_rgba(34,_34,_34,_0.5)_100%),_linear-gradient(180deg,_rgba(34,_34,_34,_0.2)_0%,_rgba(0,_0,_0,_0)_100%)] absolute top-[0] left-[0] w-full h-full",
          )}
        >
          <div className={cn(!image && "md:flex flex-col w-full items-center md:gap-xxl ")}>
            {logoVariant && logo && (
              <div
                className={`flex flex-col md:flex-row w-full items-center mb-l md:mb-0 md:pl-m md:ml-xxs rtl:md:mr-xxs rtl:md:pr-m ${centeredLogo ? "justify-center" : "justify-between"}`}
              >
                <Image
                  src={logo.url}
                  width={170}
                  height={80}
                  alt={logo.title || ""}
                  className={cn(
                    "mx-auto my-[45px] md:m-0",
                    image && "absolute top-[45%] translate-y-[-50%] left-[50%] translate-x-[-50%] z-10 md:w-5\\/24",
                  )}
                />
                <div className="flex items-center justify-between bg-gray-7 p-xxs rounded-3xl">
                  {entry.variant === "logo-cta" &&
                    podcast?.tabs.map((p: CustomLink, index: number) => (
                      <Link
                        key={index}
                        href={p.link.url}
                        className={cn("py-xxs rounded-full min-w-[162px] text-gray-70", isChecked === p.id && isActiveClass)}
                        onClick={() => setIsChecked(p.id)}
                      >
                        {p.label}
                      </Link>
                    ))}
                </div>
              </div>
            )}

            <div className={cn(image && "absolute bottom-0 mb-l z-10 left-[50%] translate-x-[-50%] w-full md:w-20\\/24")}>
              <div className="flex flex-col items-center gap-m">
                {showTitle && <h1 className={cn("font-primary uppercase text-xl ", !image ? "md:mb-0" : " font-medium")}>{title}</h1>}
                {showAbstract && <p className="font-primary uppercase">{abstract}</p>}
                {titleOrImageVariant && cta && (
                  <Button
                    href={cta?.link.url}
                    size={ButtonSize.MEDIUM}
                    variant={ButtonVariant.PRIMARY}
                    theme={ButtonTheme.WHITE}
                    customClass=" w-fit !border-white hover:!border-0"
                  >
                    {cta?.label}
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HubTemplateHero;
